
import React, { useState } from "react";
import { toast } from "react-toastify";

import { useGetCoachingCentersQuery } from "../../slices/coachingCenterApiSlice";
import { useGenerateInvoiceMutation } from "../../slices/billingApiSlice";

const GenerateInvoiceScreen = () => {
  const [selectedCoachingCenter, setSelectedCoachingCenter] = useState("");

  // =====================================================
  // Get Coaching Centers
  // =====================================================

  const {
    data: coachingCentersResponse,
    isLoading: isLoadingCoachingCenters,
    isError: isCoachingCentersError,
    error: coachingCentersError,
  } = useGetCoachingCentersQuery({
    page: 1,
    limit: 100,
  });

  console.log(coachingCentersResponse);
  const [generateInvoice, { isLoading: isGeneratingInvoice }] =
    useGenerateInvoiceMutation();

  // =====================================================
  // Normalize Coaching Center Response
  // =====================================================

  const coachingCenters = Array.isArray(coachingCentersResponse)
  ? coachingCentersResponse
  : coachingCentersResponse?.data?.coachingCenters ||
    coachingCentersResponse?.coachingCenters ||
    coachingCentersResponse?.data ||
    [];

  // =====================================================
  // Generate Invoice
  // =====================================================

  const generateHandler = async () => {
    if (!selectedCoachingCenter) {
      toast.error("Please select a coaching center");
      return;
    }

    try {
      const response = await generateInvoice(
        selectedCoachingCenter,
      ).unwrap();

      const invoice = response?.data || response;

      const invoiceNumber = invoice?.invoiceNumber;

      toast.success(
        invoiceNumber
          ? `Invoice ${invoiceNumber} generated successfully`
          : "Invoice generated successfully",
      );

      // Clear selection after successful generation
      setSelectedCoachingCenter("");
    } catch (error) {
      console.error("Unable to generate invoice:", error);

      toast.error(
        error?.data?.message ||
          error?.error ||
          "Unable to generate invoice",
      );
    }
  };

  // =====================================================
  // Coaching Centers Loading/Error State
  // =====================================================

  if (isLoadingCoachingCenters) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-center py-8">
            <svg
              className="animate-spin h-6 w-6 text-blue-600 mr-3"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />

              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <span className="text-gray-600">
              Loading coaching centers...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (isCoachingCentersError) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="font-semibold text-red-800">
              Unable to load coaching centers
            </p>

            <p className="text-sm text-red-700 mt-1">
              {coachingCentersError?.data?.message ||
                coachingCentersError?.error ||
                "Please try again later."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* =====================================================
          Header
      ===================================================== */}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Generate Invoice
        </h1>

        <p className="text-gray-500 mt-1">
          Select a coaching center to generate its billing invoice.
        </p>
      </div>

      {/* =====================================================
          Form
      ===================================================== */}

      <div className="bg-white rounded-xl shadow p-6">
        {/* ===================================================
            Coaching Center Dropdown
        =================================================== */}

        <div className="mb-6">
          <label
            htmlFor="coachingCenter"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Select Coaching Center
          </label>

          <select
            id="coachingCenter"
            value={selectedCoachingCenter}
            onChange={(e) => setSelectedCoachingCenter(e.target.value)}
            disabled={isGeneratingInvoice}
            className="
              w-full
              rounded-lg
              border
              border-gray-300
              bg-white
              px-4
              py-3
              text-gray-800
              shadow-sm
              outline-none
              transition
              focus:border-blue-500
              focus:ring-2
              focus:ring-blue-100
              disabled:bg-gray-100
              disabled:cursor-not-allowed
            "
          >
            <option value="">
              -- Select Coaching Center --
            </option>

            {Array.isArray(coachingCenters) &&
              coachingCenters.map((center) => (
                <option key={center._id} value={center._id}>
                  {center.name}
                </option>
              ))}
          </select>

          {Array.isArray(coachingCenters) &&
            coachingCenters.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                No coaching centers found.
              </p>
            )}
        </div>

        {/* ===================================================
            Selected Center Information
        =================================================== */}

        {selectedCoachingCenter && (
          <div
            className="
              mb-6
              rounded-lg
              bg-gray-50
              border
              border-gray-200
              p-4
            "
          >
            <p className="text-sm text-gray-500">
              Selected coaching center
            </p>

            <p className="mt-1 font-semibold text-gray-800">
              {
                coachingCenters.find(
                  (center) => center._id === selectedCoachingCenter,
                )?.name
              }
            </p>
          </div>
        )}

        {/* ===================================================
            Pricing Information
        =================================================== */}

        <div
          className="
            mb-6
            rounded-lg
            bg-blue-50
            border
            border-blue-100
            p-4
          "
        >
          <p className="font-semibold text-blue-800">
            Invoice calculation
          </p>

          <p className="text-sm text-blue-700 mt-1">
            The invoice will use the current billing plan,
            coaching-center billing settings, applicable discount,
            minimum invoice amount, and GST.
          </p>

          <p className="text-sm text-blue-700 mt-2">
            Any applicable discount is captured as a snapshot on the
            generated invoice, so later changes to the coaching
            center&apos;s billing settings will not affect this
            invoice.
          </p>
        </div>

        {/* ===================================================
            Generate Button
        =================================================== */}

        <button
          type="button"
          disabled={isGeneratingInvoice || !selectedCoachingCenter}
          onClick={generateHandler}
          className={`
            w-full
            flex
            justify-center
            items-center
            gap-2
            rounded-lg
            px-5
            py-3
            font-semibold
            text-white
            transition

            ${
              isGeneratingInvoice || !selectedCoachingCenter
                ? `
                  bg-gray-400
                  cursor-not-allowed
                `
                : `
                  bg-blue-600
                  hover:bg-blue-700
                `
            }
          `}
        >
          {isGeneratingInvoice ? (
            <>
              <svg
                className="animate-spin h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />

                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>

              Generating...
            </>
          ) : (
            "Generate Invoice"
          )}
        </button>
      </div>
    </div>
  );
};

export default GenerateInvoiceScreen;

